import type { ProductModel } from '#models/product';
import Product from '#models/product';

import type { ProductRaw } from '#constants/types/algolia';
import type { ProductVariantModel } from '#models/productVariant';

import type { DirectiveBinding } from 'vue';
import { useCartStore } from '~/store/cart';
import { useProductStore } from '~/store/product';
import type { CartApiItem, OrderProduct } from '#constants/types/norce';

import { useGlobalContentStore } from '~/store/globalContent';
import { useUserStore } from '~/store/user';
import useApiFetch from '~/composables/useApiFetch';
import { useCustomerTypeCookie } from '~/composables/useGlobalCookies';

type VtrackInput = {
  product: ProductRaw,
  pageName: string,
  pageId: number,
  index: number,
}

export type DataLayerProduct = {
  index?: number;
  item_brand?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category?: string;
  item_id: string;
  item_list_id?: string; // - sidans id kristian
  item_list_name?: string; // - sidans namn
  item_name: string;
  item_variant?: string; // - färg
  price?: string;
  quantity?: number,
}

export const useProductImpressionTracking = {
  mounted: (el: HTMLElement, input: DirectiveBinding<VtrackInput>)  => {
    const productStore = useProductStore();
    if (input.value.product) {
      const observer = new IntersectionObserver(
        (entries, observer) => {

          const productItem = Product.create(input.value.product);
          entries.forEach(entry => {
            const item = createDataLayerItem(productItem, 1, input.value.index);
            if (input.value.pageName) {
              item['item_list_name'] = input.value.pageName;
              item['item_list_id'] = input.value.pageId + '';
            }
            if (entry.intersectionRatio > 0 && window.dataLayer) {
              productStore.batchTrack(item);
              observer.unobserve(entry.target);
            }
          });

        }, {
          threshold: .5, // Wait til we se at least half the observed
        });
      observer.observe(el);
    } else { console.warn('Used tracking directive without value'); }
  },
};

type DataLayerPayload = {
  algoliaUserToken?: string,
  event: string,
  client_type?: 'b2b' | 'b2c',
  link_text?: string,
  link_url?: string,
  link_classes?: string,
  user_type?: 'client' | 'sales_rep' | 'customer_support',
  ecommerce?: {
    currency: string,
    items: DataLayerProduct[]
  } | null, // null is used to reset datalayer
}

/**
 * This replaces direct usage of window.datalayer.push,
 * adds algolia tracking id
 * algoliaUserToken: 'user-1',
 */
export const pushToDataLayer = (data: DataLayerPayload) => {
  const browserTrackingCookie = useCookie('randomId', { maxAge: 3600 * 24 * 365 });
  const runtimeConfig = useRuntimeConfig();
  data.algoliaUserToken = browserTrackingCookie.value || '';
  const customerType = useCustomerTypeCookie().value;
  if (customerType) {
    data.client_type = customerType;
  }

  if (window.dataLayer) {
    window.dataLayer.push(data);
  } else {
    if (runtimeConfig.public?.gtm?.enabled) {
      console.warn('attempted useTracking pushDataLayer without datalayer');
    }
  }
};

export function sendDataLayer(
  event: string,
  items: DataLayerProduct[],
  additionalData: object = {}
) {
  const globalContent = useGlobalContentStore();

  pushToDataLayer({
    event,
    ecommerce: {
      ...additionalData,
      currency: globalContent.getCurrency,
      items,
    },
  });
}

type AdditionalCartData = {
  value?: string;
  payment_type?: string;
  shipping_tier?: string;
}

/**
 * Uses the cartstore to create an even that includes items in cart
 * @param event
 * @param additionalData
 */
export function createEventFromCart(event: string, additionalData: AdditionalCartData = {}) {
  const cartStore = useCartStore();
  const trackProducts = [] as DataLayerProduct[];
  if (!additionalData.value && cartStore.cart.summary?.price) {
    additionalData.value = cartStore.cart.summary.price;
  }
  if (!isPricePublic()) {
    delete additionalData.value;
  }
  cartStore.cart.items.forEach((item, index) => {
    trackProducts.push(
      createDataLayerItem(
        item,
        item.quantity,
        index + 1
      )
    );
  });
  sendDataLayer(event, trackProducts, additionalData);
}

/**
 * Mirrors userStore.getPrice / price components: logged-out B2B users only see prices where explicitly allowed
 */
const isPricePublic = (): boolean => {
  const userStore = useUserStore();
  const globalContent = useGlobalContentStore();
  if (globalContent.disablePricingAndPurchase || globalContent.disablePricingB2C) {
    return false;
  }
  if (userStore.isLoggedIn) {
    return true;
  }
  if (globalContent.getB2cEnabled && globalContent.getIsB2c) {
    return true;
  }
  return globalContent.getShowPriceNonLoggedInB2B;
};

const removeNullValues = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null)
  );
};

/**
 * Converts to DataLayerProduct
 * @todo - all types should have dataLayer, so remove the old color thing when that is fixed
 */
export const createDataLayerItem = (
  product: ProductModel | ProductRaw | CartApiItem | OrderProduct | ProductVariantModel,
  quantity = 1,
  index = 1
): DataLayerProduct => {
  let color = '';
  if (product.colorInfo?.code) {
    color = product.colorInfo.code;
  }
  let output = {} as DataLayerProduct;
  if (product.dataLayer) {
    output = {
      ...product.dataLayer,
      quantity,
      index,
    };
  } else {
    output = {
      item_name: product.name,
      item_id: product.partNo,
      item_variant: color,
      price: product.priceInfo.price,
      quantity,
      index,
      item_brand: product.brand?.code || '',
    } as DataLayerProduct;
  }

  product.categories?.map((i) => i.name).slice(0, 4).forEach((c, index) => {
    //typescript doesn't seem to get our slice:
    //@ts-ignore
    output[`item_category${index > 0 ? index + 1 : ''}`] = c;
  });

  if (!isPricePublic()) {
    delete output.price;
  }

  //console.log(removeNullValues(output));
  return removeNullValues(output) as DataLayerProduct;
};

/**
 * Tracks button click events and pushes data to datalayer with user type and button details
 *
 * Add attribute data-tracking-class to overwrite class attr
 *
 * @param uiEvent - The mouse event from the button click
 * @param event - The event name to track, defaults to 'button_click'
 * @param link_url - Optional URL to track for link clicks - replaces href from link
 */
export function trackButtonClick(uiEvent: UIEvent | null, event = 'button_click', link_url: string | null = null, link_text_override: string | null = null) {
  const userStore = useUserStore();

  const output = {
    event,
    user_type: 'client',
  } as DataLayerPayload;

  if (userStore.isSalesRepUser) {
    output.user_type = 'sales_rep';
  }
  if (userStore.isCustomerSuccessUser) {
    output.user_type = 'customer_support';
  }

  if (link_url) {
    output.link_url = link_url;
  }

  if (link_text_override) {
    output.link_text = link_text_override;
  }

  const target = uiEvent?.target as HTMLElement;

  if (target) {
    let link_classes = target.classList.value;
    const link_text = target.textContent?.trim();
    const trackingClass = target.getAttribute('data-tracking-class');

    if (trackingClass) {
      link_classes = trackingClass;
    }

    if (!link_url && target instanceof HTMLAnchorElement) {
      output.link_url = target.href;
    }

    if (link_classes !== '') {
      output.link_classes = link_classes;
    }
    if (!link_text_override && link_text && link_text !== '') {
      output.link_text = link_text;
    }

  }

  //console.log(output);

  pushToDataLayer(output);
}

type KlaviyoProduct = {
  productId: string,
  productName: string,
  quantity: number,
  price: number,
  category: string,
  productUrl: string,
  imageUrl: string
}

type KlaviyoPayload = {
  eventType: string,
  customerEmail: string,
  customerId: string,
  countryCode: string,
  timestamp: string,
  orderId?: string,
  totalAmount?: number,
  currency?: string,
  lineItems?: KlaviyoProduct[],
  firstName?: string,
  lastName?: string,
  phoneNumber?: string,
}

export const trackKlaviyo = async(
  eventType: 'login' | 'registration' | 'password_reset' | 'order_placed' | 'activity',
  additionalData: Partial<KlaviyoPayload> = {}
) => {
  const { apiPost, lastError, lastErrorCode } = useApiFetch();
  const userStore = useUserStore();
  const now = new Date();
  const timestamp = now.toISOString().replace(/\.\d{3}Z$/, 'Z');
  const globalContent = useGlobalContentStore();

  if (userStore.userProfile.email?.includes('@iveo')) return;

  const payload: KlaviyoPayload = {
    eventType,
    customerEmail: userStore.userProfile.email,
    customerId: userStore.userProfile.code + '',
    countryCode: globalContent.getCountryCode,
    timestamp,
    ...additionalData,
  };

  // console.log(payload);

  // * * * * FAILS QUIETLY
  try {
    const res = await apiPost('/klaviyo/events', payload);

    if (!res || !res.success) {
      console.error('Klaviyo failed A');
      console.group();
      console.log('Payload:');
      console.log(payload);
      console.log(lastErrorCode.value);
      console.log(lastError.value);
      console.groupEnd();
    } else {
      // console.log('Klaviyo success');
      // console.log(res);
    }

  } catch (e) {
    console.error('Klaviyo failed B');
    console.group();
    console.log('Payload:');
    console.log(payload);
    console.log(e);
    console.groupEnd();
  }

};
